/** Home (S20), activity (S21), locations. docs/50 §4.2, docs/52 §3.3. Home payload budget: 20 KB (docs/52 §8). */
import { z } from 'zod';
import { AppPath, Day, IsoDateTime, Month, Provenance, TimeZone, page } from '../common.ts';
import { LocationStatus, OnboardingStep, OnboardingStepState, TenantStatus } from '../enums.ts';
import { LocationId, TenantId } from '../ids.ts';
import { LedgerItem } from './shared.ts';

export const QuickAction = z.enum(['ask_review', 'add_job', 'change_hours', 'new_request']);

export const NeedsYouCard = z.object({
  kind: z.enum(['approval', 'decision', 'review_reply', 'photo_nudge', 'onboarding_step', 'payment', 'holiday']),
  title: z.string().max(140),
  actionLabel: z.string().max(40),
  link: AppPath,
});

export const MonthMetric = z.object({
  key: z.enum(['site_leads', 'google_calls', 'new_reviews', 'jobs_published', 'call_taps']),
  value: z.number().int().min(0),
  lastMonth: z.number().int().min(0).nullable(),
  provenance: Provenance,
});

export const MapHeadline = z.object({
  serviceSlug: z.string().max(64),
  keyword: z.string().max(120),
  top3Points: z.number().int().min(0).max(81),
  totalPoints: z.literal(81),
  dayOnePoints: z.number().int().min(0).max(81).nullable(),
  runAt: IsoDateTime,
});

/**
 * The 7 day go-live clock as the client sees it (deck rulings c007, c008, c041): it starts the day the domain is
 * connected and pauses while we wait on them (brand check over 48 hours, go-live approval over 24 hours, photos).
 * Built from onboarding_runs and clock_intervals, which WP-JOBS writes.
 */
export const OnboardingClock = z.object({
  startedOn: Day,
  promiseDays: z.literal(7),
  /** Whole days counted against the 7, pauses taken out. */
  daysUsed: z.number().int().min(0),
  /** Whole days the clock stood still while we waited on the client. */
  daysPaused: z.number().int().min(0),
  /** The go-live date with every pause added on. */
  deadline: Day,
  /** True while the clock is paused right now. */
  pausedNow: z.boolean(),
  /** The step we're waiting on when it's paused, when we know it. */
  waitingOn: OnboardingStep.nullable(),
});

export const GetLiveSummary = z.object({
  /** Only the client's steps count toward the ring (docs/50 critique 3.6). */
  ring: z.object({ done: z.number().int().min(0), total: z.number().int().min(1) }),
  next: z.object({ step: OnboardingStep, title: z.string().max(120), actionLabel: z.string().max(40), link: AppPath }).nullable(),
  waitingOnYou: z.array(z.object({ step: OnboardingStep, label: z.string().max(80), state: OnboardingStepState })),
  weAreOnIt: z.array(z.object({ key: z.string().max(40), label: z.string().max(80), done: z.boolean() })),
  goLiveBy: z.iso.date().nullable(),
  waitingOnYouDays: z.number().int().min(0),
  previewUrl: z.string().max(2048).nullable(),
  /** Null until the domain is connected (the clock hasn't started). */
  clock: OnboardingClock.nullable().optional(),
});

/**
 * The weeks (1 to 12) and months (1 to 3) of the 90 day window the client told us had no jobs (r01). They never
 * count against the two jobs: a marked week counts as kept. Jobs counts them in `twoJobsHabit` (apps/jobs care.ts).
 * Stored in `no_jobs_marks` (migration 0007); the owner or a manager marks and unmarks them (`two_jobs.mark`).
 */
export const NoJobsPeriods = z.object({
  weeks: z.array(z.number().int().min(1).max(12)).max(12),
  months: z.array(z.number().int().min(1).max(3)).max(3),
});
export type NoJobsPeriods = z.infer<typeof NoJobsPeriods>;

/** The client marks one week or month as having had no jobs (r01). For the route WP-API adds. */
export const NoJobsMarkBody = z.discriminatedUnion('period', [
  z.object({ period: z.literal('week'), n: z.number().int().min(1).max(12), note: z.string().max(200).optional() }).strict(),
  z.object({ period: z.literal('month'), n: z.number().int().min(1).max(3), note: z.string().max(200).optional() }).strict(),
]);
export type NoJobsMarkBody = z.infer<typeof NoJobsMarkBody>;

/** One mark in a path, for undoing it: `week-1` to `week-12`, `month-1` to `month-3`. */
export const NoJobsMarkRef = z
  .string()
  .regex(/^(week-([1-9]|1[0-2])|month-[1-3])$/)
  .meta({ examples: ['week-3', 'month-1'] });
export function noJobsMarkRef(m: { period: 'week' | 'month'; n: number }): string {
  return `${m.period}-${m.n}`;
}
export function parseNoJobsMarkRef(ref: string): { period: 'week' | 'month'; n: number } {
  const [period, n] = NoJobsMarkRef.parse(ref).split('-') as ['week' | 'month', string];
  return { period, n: Number(n) };
}

/**
 * "Your two jobs" as a weekly habit (deck rulings c012, c042): over the 90 days after go-live, a review ask sent in
 * at least 10 of the 12 weeks and photos added in at least 2 of the 3 months.
 */
export const TwoJobs = z.object({
  /** Weeks and months marked as having no jobs, already counted as kept in `done` (r01). */
  noJobs: NoJobsPeriods.optional(),
  reviewWeeks: z.object({ done: z.number().int().min(0).max(13), total: z.literal(12), target: z.literal(10) }),
  photoMonths: z.object({ done: z.number().int().min(0).max(3), total: z.literal(3), target: z.literal(2) }),
  /** The current week of the window, 1 to 13 (day 85 to 90 falls in a short 13th week). */
  week: z.number().int().min(1).max(13),
  month: z.number().int().min(1).max(3),
  thisWeekAsked: z.boolean(),
  thisMonthPhotos: z.boolean(),
  /** The weeks (1 to 13) with a review ask and the months (1 to 3) with photos, so the card can offer "no jobs" only where nothing happened (r01). */
  weeksAsked: z.array(z.number().int().min(1).max(13)).max(13).optional(),
  monthsWithPhotos: z.array(z.number().int().min(1).max(3)).max(3).optional(),
  /** What counted this week (r01): asks sent or waiting to send, and taps on the tracked review link. */
  asksThisWeek: z.number().int().min(0).optional(),
  tapsThisWeek: z.number().int().min(0).optional(),
  endsOn: Day,
});

export const Home = z.object({
  tenant: z.object({ id: TenantId, name: z.string().max(60), status: TenantStatus, timeZone: TimeZone.nullable(), siteEnabled: z.boolean() }),
  mode: z.enum(['onboarding', 'live']),
  greetingName: z.string().max(80).nullable(),
  since: IsoDateTime.nullable(),
  newSince: z.object({
    leads: z.number().int().min(0),
    reviews: z.number().int().min(0),
    jobsPublished: z.number().int().min(0),
    items: z.array(LedgerItem).max(20),
  }),
  needsYou: z.array(NeedsYouCard).max(2),
  getLive: GetLiveSummary.nullable(),
  month: z.object({ month: Month, metrics: z.array(MonthMetric) }).nullable(),
  map: MapHeadline.nullable(),
  whatWeDid: z.array(LedgerItem).max(5),
  /** Shown for the 90 days after go-live. Null otherwise. */
  twoJobs: TwoJobs.nullable().optional(),
  quickActions: z.array(QuickAction),
  banner: z
    .object({ kind: z.enum(['payment_failed', 'work_paused', 'write_frozen', 'site_down']), text: z.string().max(200), link: AppPath.nullable() })
    .nullable(),
  updatedAt: IsoDateTime,
});
export type Home = z.infer<typeof Home>;

export const ActivityPage = page(LedgerItem);

export const Location = z.object({
  id: LocationId,
  name: z.string().max(80),
  status: LocationStatus,
  timeZone: TimeZone,
  publicPhone: z.string().max(32).nullable(),
  reviewLink: z.string().max(2048).nullable(),
});
export const LocationList = z.object({ items: z.array(Location) });
